import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getCsrfQueryKey, useGetCsrfQuery } from 'api/queries/common/csrf';
import { CsrfStorage } from 'api/config/csrf';
import { GetCSRFResponse } from 'api/queries/common/csrf.types';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from 'utils/constants';
import { useLogoutMutation } from 'api/mutations/auth/logout';
import { appLogout } from 'utils/session';
import { initialDataSettings, useGetAppSettingsQuery } from 'api/queries/common/config';
import { isOAuthFacebookEnabledInEnv, isOAuthGoogleEnabledInEnv } from 'utils/oauth';
import { useSessionCheckQuery } from 'api/queries/customer/session-check';
import { AppStateContextType, AppStateContextValuesType } from './types';

const defaultContextValue: AppStateContextType = {
  locale: DEFAULT_LOCALE,
  currency: DEFAULT_CURRENCY,
  isAuthorised: false,
  setIsAuthorised() {},
  logout: (_redirectTo?: string) => {},
  csrfReady: false,
  blockRequests: false,
  setBlockRequests() {},
  settings: initialDataSettings,
  isOAuthGoogleAvailable: false,
  isOAuthFacebookAvailable: false,
};

const AppStateContext = createContext<AppStateContextType>(defaultContextValue);

export default function AppStateProvider({ children }: PropsWithChildren) {
  const [values] = useState<AppStateContextValuesType>(defaultContextValue);
  const [isAuthorised, setIsAuthorised] = useState<boolean>(false);
  const [blockRequests, setBlockRequests] = useState(false);

  const { data: csrfResult } = useGetCsrfQuery();
  const { mutateAsync: logoutUser } = useLogoutMutation();

  const queryClient = useQueryClient();

  useEffect(() => {
    CsrfStorage.onChange = (v) => {
      queryClient.setQueryData(getCsrfQueryKey(), (oldData?: GetCSRFResponse) =>
        oldData ? ({ ...oldData, meta: { ...oldData?.meta, csrf: v } } as GetCSRFResponse) : oldData,
      );
    };
  }, [queryClient, values.locale, values.currency]);

  // check for token-based auth immediately (avoids flash of login page)
  const hasToken = !!localStorage.getItem('access_token');

  useLayoutEffect(() => {
    if (hasToken) {
      setIsAuthorised(true);
    }
  }, [hasToken]);

  // for session-based auth (no token), probe /api/me once CSRF is ready;
  // a successful response (true) means a valid session cookie exists;
  // uses a dedicated query that catches 401 internally to avoid the
  // global appLogout handler triggering a redirect loop for guests
  const sessionProbeEnabled = !hasToken && !!csrfResult?.success && !isAuthorised;
  const { data: sessionValid } = useSessionCheckQuery(sessionProbeEnabled);

  useEffect(() => {
    if (true === sessionValid && !isAuthorised) {
      setIsAuthorised(true);
    }
  }, [sessionValid, isAuthorised]);

  const logout = useCallback(
    (redirectTo?: string) => {
      logoutUser()
        .catch(console.error)
        .finally(() => appLogout(redirectTo));
    },
    [logoutUser],
  );

  const { data: settings } = useGetAppSettingsQuery();

  const isOAuthGoogleAvailable = useMemo(() => {
    return isOAuthGoogleEnabledInEnv() && (settings?.oauth?.google?.enabled ?? true);
  }, [settings]);

  const isOAuthFacebookAvailable = useMemo(() => {
    return isOAuthFacebookEnabledInEnv() && (settings?.oauth?.facebook?.enabled ?? true);
  }, [settings]);

  return (
    <AppStateContext.Provider
      value={{
        ...values,
        isAuthorised,
        setIsAuthorised,
        blockRequests,
        setBlockRequests,
        logout,
        csrfReady: !!csrfResult?.success,
        settings: settings || initialDataSettings,
        isOAuthGoogleAvailable,
        isOAuthFacebookAvailable,
      }}
    >
      {children}
    </AppStateContext.Provider>
  );
}

export const useAppState = () => useContext(AppStateContext);
